import type { ReactNode } from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import { StatusTag, campaignStatusTone, type StatusTone } from '../design/StatusTag';

/** Секция-карточка. flush — тело без отступов (таблицы); titleAs — уровень заголовка. */
export function Panel({
  title,
  subtitle,
  extra,
  children,
  className = '',
  flush = false,
  titleAs = 'h2',
}: {
  title?: string;
  subtitle?: string;
  extra?: ReactNode;
  children: ReactNode;
  className?: string;
  flush?: boolean;
  titleAs?: 'h2' | 'h3';
}) {
  const Title = titleAs;
  return (
    <section className={`mk-panel ${flush ? 'mk-flush ' : ''}${className}`.trim()}>
      {title && (
        <div className="mk-panel-head">
          <div>
            <Title>{title}</Title>
            {subtitle && <p>{subtitle}</p>}
          </div>
          {extra}
        </div>
      )}
      <div className="mk-panel-body">{children}</div>
    </section>
  );
}

export function QueryError({
  error,
  retry,
}: {
  error: unknown;
  retry: () => void;
}) {
  return (
    <div className="mk-hint mk-error" role="alert">
      <div>
        {error instanceof Error
          ? error.message
          : 'Не удалось загрузить данные.'}
      </div>
      <button type="button" className="mk-button mk-space" onClick={retry}>
        <ReloadOutlined aria-hidden="true" /> Повторить
      </button>
    </div>
  );
}

export function Loading({ text = 'Загружаем данные…' }: { text?: string }) {
  return (
    <div className="mk-loading" role="status" aria-live="polite">
      {text}
    </div>
  );
}

/** Простое пустое состояние (компактное). Для иллюстрации и кнопки — design/EmptyState. */
export function Empty({ children }: { children: ReactNode }) {
  return <div className="mk-empty fk-empty fk-empty-compact">{children}</div>;
}

export const STATUS_LABEL: Record<string, string> = {
  awaiting_payment: 'Ожидает оплаты',
  partially_paid: 'Оплачен частично',
  paid: 'Оплачен',
  unknown: 'Статус уточняется',
  sent: 'Отправлено',
  delivered: 'Доставлено',
  queued: 'В очереди',
  pending: 'Ожидает',
  refunded: 'Возврат',
  rejected: 'Отклонено',
  draft: 'Черновик',
  active: 'Работает',
  scheduled: 'Запланирована',
  running: 'Работает',
  sending: 'Отправляется',
  paused: 'На паузе',
  cancelled: 'Отменена',
  canceled: 'Отменена',
  completed: 'Завершена',
  done: 'Завершена',
  stopped: 'Остановлена',
  failed: 'Ошибка',
};

/** Тон для статусов вне кампаний (счета, сообщения). */
const OTHER_TONE: Record<string, StatusTone> = {
  paid: 'success',
  delivered: 'success',
  awaiting_payment: 'warning',
  partially_paid: 'warning',
  unknown: 'warning',
  rejected: 'error',
  refunded: 'neutral',
};

/** Бейдж статуса кампании/счёта: классы mk-chip green/amber/indigo/red/blue/'' сохраняются. */
export function Status({ status }: { status: string }) {
  const campaignTone = campaignStatusTone(status);
  const tone = campaignTone !== 'neutral' ? campaignTone : (OTHER_TONE[status] ?? 'info');
  return <StatusTag tone={tone}>{STATUS_LABEL[status] ?? status}</StatusTag>;
}

export function dateLabel(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' })
    : '—';
}

/** Только дата dd.MM.yyyy (счета, серии промокодов). */
export function dateOnlyLabel(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
    : '—';
}
